import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import Divider from "@mui/material/Divider";
import Pagination from "@mui/material/Pagination";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-hot-toast";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import HtmlNoteEditor from "components/HtmlNoteEditor";
import CustomerNoteContent from "components/CustomerNoteContent";
import PageErrorAlert, { PAGE_ERROR_TITLES } from "components/PageErrorAlert";
import { api } from "utils/api";
import { formatDateTime } from "utils/dateFormatter";
import {
  copyHtmlToClipboard,
  isHtmlCustomerNote,
  normalizeCustomerNoteForSave,
} from "utils/customerNoteContent";

const NOTES_PER_PAGE = 5;

const CustomerNotesPanel = forwardRef(function CustomerNotesPanel(
  { customerId, canAddNotes, canManageNotes },
  ref
) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [noteContent, setNoteContent] = useState("");
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteContent, setEditNoteContent] = useState("");
  const [deletingNote, setDeletingNote] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const parsedCustomerId = Number(customerId);

  const fetchNotes = useCallback(async () => {
    if (!Number.isFinite(parsedCustomerId) || parsedCustomerId <= 0) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await api.post("/db/notes/filter", {
        filters: { customer_id: parsedCustomerId },
        columns: ["id", "customer_id", "date", "note"],
        order_by: "date DESC",
      });
      setNotes(Array.isArray(response) ? response : []);
    } catch (err) {
      setError(err.message || "Failed to fetch customer notes.");
      setNotes([]);
    }
    setLoading(false);
  }, [parsedCustomerId]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  useImperativeHandle(ref, () => ({
    openAddModal: () => {
      if (!canAddNotes) {
        return;
      }
      setNoteContent("");
      setOpenAddModal(true);
    },
  }));

  const handleCloseAddModal = () => setOpenAddModal(false);

  const handleAddSubmit = async () => {
    const content = normalizeCustomerNoteForSave(noteContent);
    if (!content) {
      toast.error("Note content cannot be empty.");
      return;
    }
    toast.loading("Adding note...");
    try {
      const response = await api.post("/db/notes", {
        customer_id: parsedCustomerId,
        date: formatDateTime(new Date()),
        note: content,
      });
      toast.dismiss();
      if (response && response.id) {
        toast.success("Note added successfully!");
        fetchNotes();
        handleCloseAddModal();
      } else {
        toast.error("Failed to add note. Unexpected server response.");
      }
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.error || err.message || "Failed to add note.");
    }
  };

  const handleOpenEditModal = (note) => {
    setEditingNote(note);
    setEditNoteContent(note?.note || "");
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setEditingNote(null);
  };

  const handleEditSubmit = async () => {
    if (!editingNote?.id) {
      return;
    }
    const content = normalizeCustomerNoteForSave(editNoteContent);
    if (!content) {
      toast.error("Note content cannot be empty.");
      return;
    }
    toast.loading("Updating note...");
    try {
      await api.put(`/db/notes/${editingNote.id}`, { note: content });
      toast.dismiss();
      toast.success("Note updated.");
      fetchNotes();
      handleCloseEditModal();
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.error || err.message || "Failed to update note.");
    }
  };

  const handleCopyHtml = async (note) => {
    try {
      await copyHtmlToClipboard(note.note);
      toast.success("Note HTML copied to clipboard.");
    } catch (err) {
      toast.error(err?.message || "Failed to copy HTML.");
    }
  };

  const handleOpenDeleteModal = (note) => {
    if (!canManageNotes) {
      return;
    }
    setDeletingNote(note);
  };

  const handleCloseDeleteModal = () => {
    if (deleteLoading) {
      return;
    }
    setDeletingNote(null);
  };

  const handleDeleteSubmit = async () => {
    if (!deletingNote?.id) {
      return;
    }
    setDeleteLoading(true);
    toast.loading("Deleting note...");
    try {
      await api.delete(`/db/notes/${deletingNote.id}`);
      toast.dismiss();
      toast.success("Note deleted.");
      if (editingNote?.id === deletingNote.id) {
        handleCloseEditModal();
      }
      const nextCount = notes.length - 1;
      const maxPage = Math.max(1, Math.ceil(nextCount / NOTES_PER_PAGE));
      setCurrentPage((page) => Math.min(page, maxPage));
      setDeletingNote(null);
      fetchNotes();
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.error || err.message || "Failed to delete note.");
    }
    setDeleteLoading(false);
  };

  const indexOfLastNote = currentPage * NOTES_PER_PAGE;
  const indexOfFirstNote = indexOfLastNote - NOTES_PER_PAGE;
  const notesToDisplay = notes.slice(indexOfFirstNote, indexOfLastNote);
  const totalPages = Math.ceil(notes.length / NOTES_PER_PAGE);

  return (
    <>
      <Card sx={{ mt: 2 }}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={2}
          sx={{ borderBottom: "1px solid", borderColor: "divider", backgroundColor: "grey.200" }}
        >
          <MDTypography variant="h6">Customer Notes</MDTypography>
        </MDBox>
        <MDBox p={2}>
          {loading && (
            <MDBox display="flex" justifyContent="center" p={2}>
              <CircularProgress size={30} />
            </MDBox>
          )}
          {!loading && error && (
            <PageErrorAlert title={PAGE_ERROR_TITLES.customerNotes} message={error} />
          )}
          {!loading && !error && notes.length === 0 && (
            <MDTypography variant="body2" color="text" textAlign="center" p={2}>
              No notes yet for this customer.
            </MDTypography>
          )}
          {!loading && !error && notes.length > 0 && (
            <>
              {notesToDisplay.map((note) => (
                <MDBox
                  key={note.id}
                  mb={2}
                  p={1.5}
                  sx={{ border: "1px solid #eee", borderRadius: "4px", "&:last-child": { mb: 0 } }}
                >
                  <CustomerNoteContent note={note} />
                  <Divider sx={{ my: 1 }} />
                  <Grid container spacing={1} justifyContent="space-between" alignItems="center">
                    <Grid item>
                      <MDTypography variant="caption" color="text">
                        {formatDateTime(note.date)}
                      </MDTypography>
                    </Grid>
                    <Grid item sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      {isHtmlCustomerNote(note) ? (
                        <MDButton variant="text" color="info" size="small" onClick={() => handleCopyHtml(note)}>
                          Copy HTML
                        </MDButton>
                      ) : null}
                      {canManageNotes ? (
                        <>
                          <MDButton variant="text" color="dark" size="small" onClick={() => handleOpenEditModal(note)}>
                            Edit
                          </MDButton>
                          <MDButton variant="text" color="error" size="small" onClick={() => handleOpenDeleteModal(note)}>
                            Delete
                          </MDButton>
                        </>
                      ) : null}
                    </Grid>
                  </Grid>
                </MDBox>
              ))}
              {totalPages > 1 && (
                <MDBox display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(event, value) => setCurrentPage(value)}
                    color="primary"
                  />
                </MDBox>
              )}
            </>
          )}
        </MDBox>
      </Card>

      <Dialog open={openAddModal} onClose={handleCloseAddModal} fullWidth maxWidth="md">
        <DialogTitle>Add Customer Note</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter formatted note content for this customer. Notes support HTML formatting and tables.
          </DialogContentText>
          <HtmlNoteEditor
            key={openAddModal ? "add-customer-note" : "add-customer-note-closed"}
            value={noteContent}
            onChange={setNoteContent}
            placeholder="Enter customer note with formatting and tables..."
            minHeight={220}
          />
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 2.8 }}>
          <MDButton onClick={handleCloseAddModal} color="secondary">
            Cancel
          </MDButton>
          <MDButton onClick={handleAddSubmit} variant="gradient" color="info">
            Add Note
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openEditModal} onClose={handleCloseEditModal} fullWidth maxWidth="md">
        <DialogTitle>Edit Customer Note</DialogTitle>
        <DialogContent>
          <HtmlNoteEditor
            key={editingNote?.id || "edit-customer-note"}
            value={editNoteContent}
            onChange={setEditNoteContent}
            placeholder="Edit customer note..."
            minHeight={280}
          />
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 2.8 }}>
          <MDButton onClick={handleCloseEditModal} color="secondary">
            Cancel
          </MDButton>
          <MDButton onClick={handleEditSubmit} variant="gradient" color="info">
            Save Note
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deletingNote)} onClose={handleCloseDeleteModal} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Customer Note</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this note? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 2.8 }}>
          <MDButton onClick={handleCloseDeleteModal} color="secondary" disabled={deleteLoading}>
            Cancel
          </MDButton>
          <MDButton onClick={handleDeleteSubmit} variant="gradient" color="error" disabled={deleteLoading}>
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
});

CustomerNotesPanel.propTypes = {
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  canAddNotes: PropTypes.bool,
  canManageNotes: PropTypes.bool,
};

CustomerNotesPanel.defaultProps = {
  canAddNotes: false,
  canManageNotes: false,
};

export default CustomerNotesPanel;
